/**
 * @file tool-coordinator.ts
 * @description Coordinates tool switching, commit/cancel drawing actions, and synchronizes status bar/eye dropper.
 */

import { g, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { handleEyeDropper } from './eye-dropper-coordinator';
import { 
    updateDrawingStatusBar, 
    updateBasicStatusBar, 
    initStatusBarEvents 
} from './status-bar-coordinator';

// Re-export for convenience of other modules
export { handleEyeDropper };

/**
 * @function initToolCoordinator
 * @description Initializes global drawing commit/cancel logic and event subscriptions for tool changes.
 * @returns {void}
 */
export function initToolCoordinator(): void {
    // [CRITICAL_LOGIC_START]
    // STEP 1: Define Global Commit/Cancel Drawing Actions
    window.commitDrawing = function(): void {
        const currentToolId = (g.current_tool as any)?.id;
        const vectorToolManager = (window as any).vectorToolManager;
        
        if (vectorToolManager && vectorToolManager.isEditing) {
            vectorToolManager.commitEdit();
        } else if (g.isSelectionActive && (g as any).settings?.selection?.ghostMode) {
            finalizeSelectionGhostMode();
        } else {
            EventBus.emit('FINISH_DRAWING');
        }

        g.drawing = false;
        hideDrawingInfo();
        EventBus.emit('RENDER_LAYERS');
    };

    window.cancelDrawing = function(): void {
        const vectorToolManager = (window as any).vectorToolManager;
        if (vectorToolManager && vectorToolManager.isEditing) {
            vectorToolManager.cancelEdit();
        } else if (g.isSelectionActive) {
            if (typeof window.deselect === 'function') {
                window.deselect();
            }
            finalizeSelectionGhostMode();
        } else {
            g.drawing = false;
        }

        hideDrawingInfo();
        EventBus.emit('RENDER_LAYERS');
    };
    // [CRITICAL_LOGIC_END]

    // STEP 2: Initialize Sub-Coordinator Events
    initStatusBarEvents();
    bindActionButtons();

    // STEP 3: Event Bus Subscriptions
    EventBus.on('TOOL_CHANGED', (data: { toolId: string }) => {
        handleToolChangeNotification(data.toolId);
    });

    EventBus.on('CANVAS_MOUSE_MOVE', (event: MouseEvent) => {
        updateDrawingStatusBar(event);
        handleEyeDropper(event);
        updateBasicStatusBar();
    });

    EventBus.on('DRAWING_STATUS_UPDATE', () => {
        updateDrawingStatusBar();
    });

    EventBus.on('CANVAS_MOUSE_DOWN', (event: MouseEvent) => {
        handleEyeDropper(event);
    });

    // STEP 4: Initial Tool Sync
    syncInitialGhostModeState();
}

/**
 * @function finalizeSelectionGhostMode
 * @description Cleans up selection-specific ghost mode states.
 * @private
 */
function finalizeSelectionGhostMode(): void {
    g.drawing = false;
    window.polyPoints = [];
    window.lassoPoints = [];
    (g as any)._selectionEditBounds = null;
    (g as any)._lassoEditPoints = null;
    (g as any)._selectionBaseCanvas = null;
    (g as any)._draggingPointIndex = -1;
    (g as any)._draggingCornerIndex = -1;
}

/**
 * @function hideDrawingInfo
 * @description Hides the drawing info layout.
 * @private
 */
function hideDrawingInfo(): void {
    const drawingInfo = document.getElementById('drawingInfo');
    if (drawingInfo) {
        drawingInfo.style.display = 'none';
    }
}

import { loadToolSettings, syncSettingsToState } from '@hcie/shared';

/**
 * @function handleToolChangeNotification
 * @description Logic executed when a tool is changed.
 * @private
 */
function handleToolChangeNotification(toolId: string): void {
    // [CRITICAL_LOGIC_START]
    // 1. Load and sync tool-specific persistent settings (#2095)
    const settings = loadToolSettings();
    syncSettingsToState(settings, g as any);
    EventBus.emit('SETTINGS_LOADED'); // Tell UI to refresh sliders from 'g'

    const selectionToolIds = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-single-row-select', 'btn-single-column-select', 'btn-lasso', 'btn-poly-select',
        'RectSelect', 'EllipseSelect', 'CircleSelect', 'Lasso', 'PolySelect'
    ];
    const isSelectionTool: boolean = selectionToolIds.some(id => toolId === id);

    if (!(g as any).settings) (g as any).settings = {};
    if (!(g as any).settings.selection) (g as any).settings.selection = {};

    // Ghost Mode is enabled for selection tools by default.
    if (isSelectionTool) {
        (g as any).settings.selection.ghostMode = true;
    }

    const chkGhostMode = document.getElementById('chkGhostMode') as HTMLInputElement | null;
    if (chkGhostMode) {
        chkGhostMode.checked = !!(g as any).settings.selection.ghostMode;
    }
    
    updateBasicStatusBar();
    // [CRITICAL_LOGIC_END]
}

/**
 * @function bindActionButtons
 * @description Binds the global commit/cancel functions to UI buttons.
 * @private
 */
function bindActionButtons(): void {
    const btnCommit = document.getElementById('btnCommitDrawing');
    const btnCancel = document.getElementById('btnCancelDrawing');
    if (btnCommit) {
        btnCommit.addEventListener('click', () => window.commitDrawing?.());
    }
    if (btnCancel) {
        btnCancel.addEventListener('click', () => window.cancelDrawing?.());
    }

    const chkGhostMode = document.getElementById('chkGhostMode') as HTMLInputElement | null;
    if (chkGhostMode) {
        chkGhostMode.addEventListener('change', () => {
            if (!(g as any).settings) (g as any).settings = {};
            if (!(g as any).settings.selection) (g as any).settings.selection = {};
            (g as any).settings.selection.ghostMode = chkGhostMode.checked;

            if (!chkGhostMode.checked) {
                window.commitDrawing?.();
            }
        });
    }

    // Pen Pressure toggle (#2082)
    const pressureToggle = document.getElementById('pressureToggle') as HTMLInputElement | null;
    if (pressureToggle) {
        // Initialize from persisted state
        const saved = localStorage.getItem('hcie_pressure_enabled');
        if (saved !== null) {
            pressureToggle.checked = saved !== 'false';
        }
        (g as any).pressure_enabled = pressureToggle.checked;

        pressureToggle.addEventListener('change', () => {
            (g as any).pressure_enabled = pressureToggle.checked;
            localStorage.setItem('hcie_pressure_enabled', String(pressureToggle.checked));
        });
    }
}

/**
 * @function syncInitialGhostModeState
 * @description Synchronizes initial ghost mode state for the current tool.
 * @private
 */
function syncInitialGhostModeState(): void {
    const selectionTools = ['btn-rect-select', 'btn-ellipse-select', 'btn-circle-select', 'btn-lasso', 'btn-poly-select'];
    const isSelectionTool: boolean = selectionTools.includes(g.current_tool.id);
    
    if (!(g as any).settings) (g as any).settings = {};
    if (!(g as any).settings.selection) (g as any).settings.selection = {};
    
    (g as any).settings.selection.ghostMode = isSelectionTool;
    
    const chkGhostMode = document.getElementById('chkGhostMode') as HTMLInputElement | null;
    if (chkGhostMode) {
        chkGhostMode.checked = isSelectionTool;
    }
}
